import {Component} from '@angular/core';
import {AppearanceService} from '../../core/services/appearance.service';
import {
  AppearanceSettingsInterface, COMPONENT_THEMES, MENU_THEMES, TOPBAR_THEMES,
} from '../../api/interfaces/appearance-settings.interface';
import {MenuService} from './app.menu.service';

@Component({
  selector: 'app-config',
  template: `
    <p-sidebar [(visible)]="appMenuService.configMenuActive" [position]="appearance.settings.isRTL ? 'left' : 'right'"
               [blockScroll]="true" [autoZIndex]="false" [style]="{zIndex: 6201}"
               [ariaCloseLabel]="'common.close' | translate"
               styleClass="layout-config appearance-config p-sidebar-sm fs-small p-0">
      <ng-template pTemplate="header">
        <h2 class="text-xl m-0 flex-1">{{ 'appearance.title' | translate }}</h2>
      </ng-template>
      <div class="layout-config-panel flex flex-column">
        <div class="px-3">
          <p>{{ 'appearance.description' | translate }}</p>
          <p class="text-color-secondary" aria-live="polite" role="status">
            {{ ('appearance.feedback.' + appearance.feedback) | translate }}
          </p>
          <button *ngIf="appearance.feedback === 'save_error' || appearance.feedback === 'load_error'"
                  pButton type="button" icon="pi pi-refresh" class="p-button-text"
                  (click)="appearance.retry()" [disabled]="!appearance.canEdit"
                  [label]="'appearance.retry' | translate"></button>
        </div>
        <hr class="mb-0"/>
        <div class="layout-config-options p-3">
          <h3 class="text-base">{{ 'appearance.scale' | translate }}</h3>
          <div class="flex align-items-center gap-2 mb-2">
            <p-inputSwitch inputId="appearance-auto-scale" [ngModel]="appearance.settings.scale === null"
                           [disabled]="!appearance.canEdit"
                           (ngModelChange)="change('scale', $event ? null : appearance.scale)"></p-inputSwitch>
            <label for="appearance-auto-scale">{{ 'appearance.automatic' | translate }}</label>
          </div>
          <div class="flex align-items-center">
            <button pButton pRipple type="button" icon="pi pi-minus" class="p-button-rounded p-button-text"
                    [attr.aria-label]="'appearance.smaller' | translate"
                    [disabled]="!appearance.canEdit || appearance.scale <= 14"
                    (click)="change('scale', appearance.scale - 1)"></button>
            <i *ngFor="let scale of scales" class="pi pi-circle-on m-1 scale-icon"
               [class.scale-active]="scale === appearance.scale" aria-hidden="true"></i>
            <button pButton pRipple type="button" icon="pi pi-plus" class="p-button-rounded p-button-text"
                    [attr.aria-label]="'appearance.larger' | translate"
                    [disabled]="!appearance.canEdit || appearance.scale >= 18"
                    (click)="change('scale', appearance.scale + 1)"></button>
            <span class="px-2 white-space-nowrap">{{ appearance.scale }} px</span>
          </div>
          <fieldset *ngFor="let group of groups" class="border-none p-0 my-3">
            <legend class="font-medium mb-2">{{ ('appearance.' + group.key) | translate }}</legend>
            <div class="flex flex-wrap gap-3">
              <div *ngFor="let option of group.options" class="flex align-items-center gap-2">
                <p-radioButton [name]="group.key" [value]="option" [inputId]="'appearance-' + group.key + '-' + option"
                               [ngModel]="appearance.settings[group.key]" [disabled]="!appearance.canEdit"
                               (ngModelChange)="change(group.key, $event)"></p-radioButton>
                <label [for]="'appearance-' + group.key + '-' + option">{{ ('appearance.options.' + option) | translate }}</label>
              </div>
            </div>
          </fieldset>
          <div *ngFor="let key of toggles" class="my-3 flex align-items-center gap-2">
            <p-inputSwitch [inputId]="'appearance-' + key" [ngModel]="appearance.settings[key]"
                           [disabled]="!appearance.canEdit" (ngModelChange)="change(key, $event)"></p-inputSwitch>
            <label [for]="'appearance-' + key">{{ ('appearance.' + key) | translate }}</label>
          </div>
          <fieldset *ngFor="let palette of palettes" class="border-none p-0 my-3">
            <legend class="font-medium mb-2">{{ ('appearance.' + palette.key) | translate }}</legend>
            <p *ngIf="palette.key === 'menuTheme' && appearance.settings.layoutMode === 'dark'" class="text-color-secondary">
              {{ 'appearance.dark_menu' | translate }}
            </p>
            <div class="flex flex-wrap gap-2" *ngIf="palette.key !== 'menuTheme' || appearance.settings.layoutMode === 'light'">
              <button *ngFor="let color of palette.colors" pButton pRipple type="button"
                      class="appearance-swatch p-button-outlined p-button-icon-only"
                      [class.appearance-swatch--round]="swatchShape === 'round'"
                      [disabled]="!appearance.canEdit"
                      [style.background-color]="color.color" [style.border-color]="'var(--surface-border)'"
                      [attr.title]="('appearance.colors.' + color.name) | translate"
                      [attr.aria-label]="('appearance.colors.' + color.name) | translate"
                      [attr.aria-pressed]="appearance.settings[palette.key] === color.name"
                      (click)="change(palette.key, color.name)">
                <i *ngIf="appearance.settings[palette.key] === color.name" class="pi pi-check appearance-swatch-check" aria-hidden="true"></i>
              </button>
            </div>
          </fieldset>
          <button pButton type="button" icon="pi pi-replay" class="p-button-outlined w-full mt-3"
                  [disabled]="!appearance.canEdit" (click)="appearance.reset()"
                  [label]="'appearance.reset' | translate"></button>
        </div>
      </div>
    </p-sidebar>
  `,
})
export class AppConfigComponent {
  public readonly scales = [14, 15, 16, 17, 18] as const;
  // Change this one parameter to restore round swatches without changing preferences.
  public readonly swatchShape: 'square' | 'round' = 'square';
  public readonly groups: ReadonlyArray<{key: keyof AppearanceSettingsInterface; options: string[]}> = [
    {key: 'layoutMode', options: ['light', 'dark']},
    {key: 'menuMode', options: ['static', 'overlay']},
    {key: 'inlineMenuPosition', options: ['top', 'bottom', 'both']},
    {key: 'inputStyle', options: ['outlined', 'filled']},
  ];
  public readonly toggles = ['ripple', 'isRTL'] as const;
  public readonly palettes: ReadonlyArray<{
    key: 'theme' | 'menuTheme' | 'topbarTheme'; colors: ReadonlyArray<{name: string; color: string}>;
  }> = [
    {key: 'menuTheme', colors: MENU_THEMES},
    {key: 'topbarTheme', colors: TOPBAR_THEMES},
    {key: 'theme', colors: COMPONENT_THEMES},
  ];

  constructor(public readonly appearance: AppearanceService, public readonly appMenuService: MenuService) {}

  public change(key: keyof AppearanceSettingsInterface, value: unknown): void {
    this.appearance.change({[key]: value});
  }
}
